import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'next-i18next'
import { ticketsAPI } from '../utils/api'
import { graphqlAPI } from '../utils/graphql'
import { getStoredVerificationData, isEmailVerifiedInSession, storeEmailVerification, refreshEmailVerificationExpiry } from '../utils/helpers'
import './TicketHistory.scss'

const TicketHistory = () => {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState('')
  const [step, setStep] = useState('email')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [tickets, setTickets] = useState([])
  const [resendSeconds, setResendSeconds] = useState(0)
  const [canResend, setCanResend] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const presetEmail = params.get('email')
    if (presetEmail) {
      setEmail(presetEmail)
      const cached = getStoredVerificationData()
      if (cached && cached.email === presetEmail.toLowerCase()) {
        refreshEmailVerificationExpiry(presetEmail, null)
      }
    } else {
      const cached = getStoredVerificationData()
      if (cached && cached.email) {
        setEmail(cached.email)
        if (isEmailVerifiedInSession(cached.email, null)) {
          refreshEmailVerificationExpiry(cached.email, null)
          ;(async () => {
            try {
              setLoading(true)
              const resp = await ticketsAPI.getUserTicketsByEmail(cached.email)
              const list = resp?.data?.tickets || []
              setTickets(list)
              setStep('history')
            } catch (err) {
              setError(err.message)
            } finally {
              setLoading(false)
            }
          })()
        }
      }
    }
  }, [])

  const groupedByEvent = useMemo(() => {
    const groups = {}
    for (const tkt of tickets) {
      const key = `${tkt.event_id}`
      if (!groups[key]) {
        groups[key] = {
          eventId: tkt.event_id,
          eventCode: tkt.event_code,
          eventName: tkt.event_name,
          eventDate: tkt.event_date,
          eventTime: tkt.event_time,
          eventVenue: tkt.event_venue,
          eventAddress: tkt.event_address,
          payments: {},
          tickets: []
        }
      }
      groups[key].tickets.push(tkt)
      const pid = tkt.event_payment_id
      if (!groups[key].payments[pid]) groups[key].payments[pid] = []
      groups[key].payments[pid].push(tkt)
    }
    const arr = Object.values(groups)
    arr.sort((a, b) => {
      const da = new Date(a.eventDate)
      const db = new Date(b.eventDate)
      return db - da
    })
    return arr
  }, [tickets])

  const handleSendCode = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      setError('')
      await graphqlAPI.sendHistoryVerificationCode(email, i18n.language)
      setStep('verify')
      setCanResend(false)
      setResendSeconds(60)
    } catch (err) {
      const msg = err?.message || ''
      if (msg.includes('Failed to fetch') || msg.toLowerCase().includes('network')) {
        setError(t('errors.networkError'))
      } else {
        setError(t('errors.storeVerificationFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      setError('')
      const result = await graphqlAPI.verifyEmailCode(email, code)
      if (result && result.success) {
        storeEmailVerification(email, null)
        
        // Store the auth token if present
        if (result.token) {
          sessionStorage.setItem('ticketUserToken', result.token)
        }

        const cacheKey = `history_${email}`
        const cached = sessionStorage.getItem(cacheKey)
        if (cached) {
          setTickets(JSON.parse(cached))
        } else {
          const resp = await ticketsAPI.getUserTicketsByEmail(email)
          const list = resp?.data?.tickets || []
          setTickets(list)
          sessionStorage.setItem(cacheKey, JSON.stringify(list))
        }
        setStep('history')
      } else {
        setError(t('errors.invalidOrExpiredCode'))
      }
    } catch (err) {
      const serverMessage = err?.response?.errors?.[0]?.message || err?.message || ''
      if (serverMessage.includes('Failed to fetch') || serverMessage.toLowerCase().includes('network')) {
        setError(t('errors.networkError'))
      } else {
        setError(serverMessage || t('errors.unknown'))
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (step !== 'verify') return
    if (resendSeconds <= 0) {
      setCanResend(true)
      return
    }
    const id = setTimeout(() => setResendSeconds((s) => Math.max(0, s - 1)), 1000)
    return () => clearTimeout(id)
  }, [step, resendSeconds])

  const handleResendCode = async () => {
    if (!canResend) return
    try {
      setLoading(true)
      setError('')
      await graphqlAPI.sendHistoryVerificationCode(email, i18n.language)
      setCanResend(false)
      setResendSeconds(60)
    } catch (err) {
      const msg = err?.message || ''
      if (msg.includes('Failed to fetch') || msg.toLowerCase().includes('network')) {
        setError(t('errors.networkError'))
      } else {
        setError(t('errors.storeVerificationFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  const downloadTicketsPdf = async (paymentOrOrderId) => {
    try {
      const blob = await ticketsAPI.getTicketDownloads(String(paymentOrOrderId))
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `tickets-${paymentOrOrderId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => window.URL.revokeObjectURL(url), 2000)
    } catch (err) {
      setError(err.message)
    }
  }

  const downloadReceiptPdf = async (paymentId, eventCode) => {
    try {
      const blob = await ticketsAPI.getReceipt(String(paymentId), email, i18n.language)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const prefix = t('history.receiptFilenamePrefix')
      a.download = `${prefix}-${eventCode || paymentId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => window.URL.revokeObjectURL(url), 2000)
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="ticket-history container py-4">
      {step === 'email' && (
        <form onSubmit={handleSendCode} className="mx-auto" style={{maxWidth: 560}}>
          <h2 className="mb-3">{t('history.title')}</h2>
          <p className="text-muted mb-4">{t('history.subtitle')}</p>
          <div className="mb-3">
            <label className="form-label">{t('history.emailLabel')}</label>
            <input type="email" className="form-control" value={email} onChange={(e)=>setEmail(e.target.value)} required disabled={loading} />
          </div>
          {error && <div className="alert alert-danger mb-3">{error}</div>}
          <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? t('history.sending') : t('history.sendCode')}</button>
        </form>
      )}

      {step === 'verify' && (
        <form onSubmit={handleVerify} className="mx-auto" style={{maxWidth: 560}}>
          <h2 className="mb-3">{t('history.verifyTitle')}</h2>
          <p className="text-muted mb-4">{t('history.verifySubtitle', { email })}</p>
          <div className="mb-3">
            <label className="form-label">{t('history.codeLabel')}</label>
            <input type="text" className="form-control" value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())} maxLength={6} required disabled={loading} />
          </div>
          {error && <div className="alert alert-danger mb-3">{error}</div>}
          <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? t('history.verifying') : t('history.verify')}</button>
          <div className="mt-3 d-flex align-items-center gap-2">
            {!canResend && (
              <span className="text-muted">{t('verification.resendIn', { seconds: resendSeconds })}</span>
            )}
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={!canResend || loading}
              onClick={handleResendCode}
            >
              {t('verification.resendCode')}
            </button>
          </div>
        </form>
      )}

      {step === 'history' && (
        <div>
          <h2 className="mb-3">{t('history.yourTickets')}</h2>
          {groupedByEvent.map(group => (
            <div key={group.eventId} className="card mb-3">
              <div className="card-body">
                <h4 className="card-title">{group.eventName}</h4>
                <p className="card-subtitle text-muted mb-2">{t('history.eventDate', { date: group.eventDate, time: group.eventTime || '' })}</p>
                <p className="mb-2">{group.eventVenue}{group.eventAddress ? ` • ${group.eventAddress}` : ''}</p>
                <p className="mb-3">{t('history.totalTickets', { count: group.tickets.length })}</p>

                {Object.keys(group.payments)
                  .sort((a, b) => {
                    const ta = Math.max(...group.payments[a].map(t => new Date(t.generated_at).getTime()))
                    const tb = Math.max(...group.payments[b].map(t => new Date(t.generated_at).getTime()))
                    return tb - ta
                  })
                  .map(pid => {
                    const paymentTickets = group.payments[pid] || []
                    const paymentInfo = paymentTickets[0] || {}
                    const hasTickets = paymentTickets.some(t => t.ticket_id)
                    
                    return (
                      <div key={pid} className="border rounded p-2 mb-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            {(() => {
                              const orderId = paymentInfo.order_id
                              return (
                                <>
                                  <strong>{t('history.purchase')}</strong> {orderId ? orderId : `#${pid}`}
                                  <span className="mx-2">|</span>
                                  <strong>{t('history.tickets', 'Tickets')}:</strong> {paymentTickets.filter(t => t.ticket_id).length}
                                  <span className="mx-2">|</span>
                                  <strong>Total:</strong> {Number(paymentInfo.total_amount || 0).toFixed(2)}€
                                </>
                              )
                            })()}
                          </div>
                          <div className="d-flex gap-2">
                            {hasTickets && (
                              <button className="btn btn-outline-primary btn-sm" onClick={() => {
                                const orderId = paymentInfo.order_id
                                downloadTicketsPdf(orderId || pid)
                              }}>{t('history.downloadTickets')}</button>
                            )}
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => downloadReceiptPdf(pid, group.eventCode)}>{t('history.downloadReceipt')}</button>
                          </div>
                        </div>
                        {!hasTickets && (
                          <div className="text-muted fst-italic small mt-1">
                            {t('history.noTicketsGenerated', 'No tickets generated for this payment')}
                          </div>
                        )}
                      </div>
                    )
                  })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default TicketHistory
